import tw, { css, type TwStyle } from "twin.macro";
import React from "react";
import { type RuleSet } from "styled-components";
import { Icon, type IconComponent, type IconName, type IconSize } from "~/features/ui/icon";
import { hover } from "~/features/ui/utils";

type Props = {
  variant?: keyof ReturnType<typeof getVariants>;
  size?: keyof typeof sizes;
  fullHeight?: boolean;
  hoverId?: string;
  icon: {
    component: IconComponent;
    size?: IconSize;
    style?: TwStyle | RuleSet<object>;
    onHoverStyle?: TwStyle | RuleSet<object>;
  };
  [key: string]: unknown;
};

const buttonStyle = tw`
  rounded-2 flex items-center shrink-0
  justify-center transition-[background-color, opacity] duration-250 ease-in-out
`;

const sizes = {
  xsmall: tw`w-16 h-32`,
  small: tw`w-32 h-32`,
  medium: tw`w-40 h-40`,
  large: tw`w-48 h-48`,
};

function getVariants(props: { hoverId?: string }) {
  const { hoverId } = props;

  return {
    primary: css`
      ${tw`text-white-100-light bg-green-80`}
      ${hover(hoverId, tw`bg-blue-100`)}
    `,
    grey: css`
      ${tw`text-black-100-light bg-grey-40-light`}
      ${hover(hoverId, tw`bg-blue-100`)}
    `,
    transparent: css`
      ${tw`text-white-100-light bg-none text-current`}
      ${hover(hoverId, tw`opacity-100`)}
    `,
  };
}

function getIconAnimation(props: { hoverId?: string; onHoverStyle?: TwStyle | RuleSet<object> }) {
  const { hoverId, onHoverStyle } = props;

  if (onHoverStyle) {
    return hover(
      hoverId,
      css`
        > [data-icon] {
          ${onHoverStyle}
        }
      `
    );
  }

  return null;
}

export function IconButton(props: Props & React.ComponentPropsWithRef<"button">) {
  const { icon, size = "medium", variant = "primary", fullHeight = false, hoverId, ...rest } = props;

  return (
    <button
      css={[
        buttonStyle,
        sizes[size],
        getVariants({ hoverId })[variant],
        getIconAnimation({ hoverId, onHoverStyle: icon.onHoverStyle }),
        // h-full would resolve against an auto-height flex parent (i.e. collapse to the
        // icon); stretching along the cross axis matches the parent row instead.
        fullHeight && tw`h-auto self-stretch`,
      ]}
      {...rest}
    >
      <Icon data-icon icon={icon.component} size={icon.size} css={icon.style} />
    </button>
  );
}
